import { useState, useEffect, createContext, useContext } from "react";
import { adminMe, adminLogin, adminLogout } from "../lib/adminApi";
import useProductLogo from "../lib/useProductLogo";
import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import ProfileDialog from "../components/ProfileDialog";
import {
  FileQuestion, Grid3X3, FileText, Calculator, LogOut, Lock, Zap, UserCircle,
  Database, Palette, Users, BookOpen, StickyNote, Mail, Wrench, Bot,
  ChevronDown, ChevronRight, LayoutGrid, Library, Boxes, Compass, FileSearch, ClipboardCheck,
  ScanLine, ClipboardList, HelpCircle, Package, Globe,
} from "lucide-react";

const AuthCtx = createContext(null);
export const useAdmin = () => useContext(AuthCtx);

// Sidebar is organised by TOP-LEVEL PRODUCT MODULE (max depth = 2).
// Each module contains one or more section groups; each group contains
// clickable items. Group labels are non-clickable section headers.
// Future products (SmartImprove, SmartBlueprint) slot in as new modules
// without any structural change.
const MODULES = [
  // ------------------------------------------------------------------
  // Platform — shared WarehouseX admin/knowledge items that span every
  // product module (SmartEvaluate, SmartImprove, future). Sits at the
  // top so admins land on the platform-wide controls first.
  //
  // Iter 90: Promoted Recommendation Engine, ROI Model, Implementation
  // Model, and Email Templates from SmartEvaluate to Platform → Shared
  // Services so SmartEvaluate + SmartImprove both consume one source of
  // truth. Underlying routes/components are untouched — this is a pure
  // navigation IA cleanup.
  // ------------------------------------------------------------------
  {
    id: "platform",
    label: "Platform",
    icon: LayoutGrid,
    routePrefixes: [
      "/admin/users",
      "/admin/branding",
      "/admin/reference-models",
      "/admin/recommendations",
      "/admin/roi-assumptions",
      "/admin/implementation-templates",
      "/admin/email-templates",
      "/admin/help-knowledge",
    ],
    groups: [
      {
        id: "platform-administration",
        label: "Administration",
        items: [
          { to: "/admin/users", label: "Users", icon: Users },
          { to: "/admin/branding", label: "Branding", icon: Palette },
        ],
      },
      {
        // Shared engines consumed by every product module.
        id: "platform-shared-services",
        label: "Shared Services",
        items: [
          { to: "/admin/reference-models", label: "Reference Models", icon: Library },
          { to: "/admin/recommendations", label: "Recommendation Library", icon: FileText },
          { to: "/admin/roi-assumptions", label: "ROI Models", icon: Calculator },
          { to: "/admin/implementation-templates", label: "Implementation Model", icon: Wrench },
          { to: "/admin/email-templates", label: "Email Templates", icon: Mail },
        ],
      },
      {
        id: "platform-ai-knowledge",
        label: "AI / Knowledge",
        items: [
          { to: "/admin/help-knowledge", label: "PRIVA Knowledge Hub", icon: Bot },
        ],
      },
    ],
  },
  {
    id: "smartevaluate",
    label: "SmartEvaluate",
    icon: Compass,
    routePrefixes: [
      "/admin/questions",
      "/admin/triggers",
      "/admin/capability-matrix",
      "/admin/demo-profiles",
      "/admin/validation-profiles",
      "/admin/smartevaluate/ai-analysis-prompts",
      "/admin/evaluate/client-communication",
      "/admin/guidelines",
      "/admin/logic-reference",
    ],
    groups: [
      {
        id: "engine",
        label: "Engine",
        items: [
          { to: "/admin/questions", label: "Questions", icon: FileQuestion },
          { to: "/admin/triggers", label: "Triggers", icon: Zap },
          { to: "/admin/capability-matrix", label: "Capability Model", icon: Grid3X3 },
          { to: "/admin/demo-profiles", label: "Demo Profiles", icon: Database },
          { to: "/admin/validation-profiles", label: "Validation Profiles", icon: ClipboardCheck },
        ],
      },
      {
        id: "smartevaluate-ai",
        label: "AI",
        items: [
          { to: "/admin/smartevaluate/ai-analysis-prompts", label: "AI Analysis Prompts", icon: Bot },
        ],
      },
      {
        id: "client-output",
        label: "Client Output",
        items: [
          { to: "/admin/evaluate/client-communication", label: "Client Communication", icon: Mail },
        ],
      },
      {
        id: "smartevaluate-knowledge",
        label: "Knowledge",
        items: [
          { to: "/admin/guidelines", label: "SmartEvaluate Playbook", icon: StickyNote },
          { to: "/admin/logic-reference", label: "Logic Reference", icon: BookOpen },
        ],
      },
    ],
  },
  // ------------------------------------------------------------------
  // SmartBid — RFP & Requirements Analysis. Owns its own AI prompts +
  // playbook. Prompts live in the shared repository, filtered by
  // application=SmartBid (single backend store, no duplication).
  // ------------------------------------------------------------------
  {
    id: "smartbid",
    label: "SmartBid",
    icon: FileSearch,
    routePrefixes: ["/admin/smartbid"],
    groups: [
      {
        id: "smartbid-ai",
        label: "AI",
        items: [
          { to: "/admin/smartbid/ai-analysis-prompts", label: "AI Analysis Prompts", icon: Bot },
        ],
      },
      {
        id: "smartbid-knowledge",
        label: "Knowledge",
        items: [
          { to: "/admin/smartbid/playbook", label: "SmartBid Playbook", icon: StickyNote },
        ],
      },
      {
        id: "smartbid-quality",
        label: "Quality",
        items: [
          { to: "/admin/smartbid/validation-profiles", label: "Validation Profiles", icon: ClipboardCheck },
        ],
      },
    ],
  },
  // ------------------------------------------------------------------
  // SmartInspect — Inspection digitisation ROI. Own collections + config,
  // fully isolated from SmartEvaluate / SmartImprove / SmartBid / SmartEWM.
  // ------------------------------------------------------------------
  {
    id: "smartinspect",
    label: "SmartInspect",
    icon: ScanLine,
    routePrefixes: ["/admin/smartinspect"],
    groups: [
      {
        id: "smartinspect-engine",
        label: "Engine",
        items: [
          { to: "/admin/smartinspect/questions", label: "Questions", icon: FileQuestion },
          { to: "/admin/smartinspect/roi-formulas", label: "ROI Formula Mapping", icon: Calculator },
          { to: "/admin/smartinspect/country-currency", label: "Countries & Benchmarks", icon: Globe },
          { to: "/admin/smartinspect/commercial-packages", label: "Commercial Packages", icon: Package },
        ],
      },
      {
        id: "smartinspect-client-output",
        label: "Client Output",
        items: [
          { to: "/admin/smartinspect/client-communication", label: "Client Communication", icon: Mail },
        ],
      },
      {
        id: "smartinspect-knowledge",
        label: "Knowledge",
        items: [
          { to: "/admin/smartinspect/instructions", label: "Instructions", icon: BookOpen },
          { to: "/admin/smartinspect/playbook", label: "SmartInspect Playbook", icon: StickyNote },
        ],
      },
    ],
  },
  // Iter 90: SmartImprove duplicate-shared-engines + Executive Report
  // Templates were removed from this sidebar. Routes still resolve so
  // anyone holding a deep link continues to work — only the menu entries
  // were trimmed.
  {
    id: "smartimprove",
    label: "SmartImprove",
    icon: Wrench,
    routePrefixes: ["/admin/smartimprove"],
    groups: [
      {
        id: "smartimprove-engine",
        label: "Engine",
        items: [
          { to: "/admin/smartimprove/operational-areas", label: "Business Processes", icon: LayoutGrid },
          { to: "/admin/smartimprove/issue-library", label: "Issue Templates", icon: FileQuestion },
          { to: "/admin/smartimprove/kpi-library", label: "KPI Templates", icon: Grid3X3 },
          { to: "/admin/smartimprove/insight-templates", label: "Insight Templates", icon: Zap },
          { to: "/admin/smartimprove/value-benchmarks", label: "Value Benchmarks", icon: Calculator },
          { to: "/admin/smartimprove/demo-profiles", label: "Demo Profiles", icon: Database },
        ],
      },
      {
        id: "smartimprove-ai",
        label: "AI",
        items: [
          { to: "/admin/smartimprove/ai-analysis-prompts", label: "AI Analysis Prompts", icon: Bot },
        ],
      },
      {
        id: "smartimprove-client-output",
        label: "Client Output",
        items: [
          { to: "/admin/smartimprove/client-communication", label: "Client Communication", icon: Mail },
        ],
      },
      {
        id: "smartimprove-knowledge",
        label: "Knowledge",
        items: [
          { to: "/admin/smartimprove/playbook", label: "SmartImprove Playbook", icon: StickyNote },
          { to: "/admin/smartimprove/ai-guidance", label: "Narrative Guidance", icon: BookOpen },
          { to: "/admin/smartimprove/knowledge-library", label: "Improvement Knowledge Library", icon: Library },
        ],
      },
      {
        id: "smartimprove-administration",
        label: "Administration",
        items: [
          { to: "/admin/smartimprove/engagement-defaults", label: "Engagement Defaults", icon: Database },
        ],
      },
    ],
  },
  // ------------------------------------------------------------------
  // SmartEWM — Operational Intelligence. Owns its own AI prompts +
  // playbook. Prompts live in the shared repository, filtered by
  // application=SmartEWM (single backend store, no duplication).
  // ------------------------------------------------------------------
  {
    id: "smartewm",
    label: "SmartEWM",
    icon: Boxes,
    routePrefixes: ["/admin/smartewm"],
    groups: [
      {
        id: "smartewm-ai",
        label: "AI",
        items: [
          { to: "/admin/smartewm/ai-analysis-prompts", label: "AI Analysis Prompts", icon: Bot },
        ],
      },
      {
        id: "smartewm-knowledge",
        label: "Knowledge",
        items: [
          { to: "/admin/smartewm/playbook", label: "SmartEWM Playbook", icon: StickyNote },
        ],
      },
    ],
  },
];

// Detect which module owns the current pathname. Used to drive sidebar
// auto-expand / auto-highlight from the route (the source of truth) so the
// section never collapses when a child link is clicked.
function detectActiveModule(pathname) {
  for (const mod of MODULES) {
    const prefixes = mod.routePrefixes || [];
    for (const p of prefixes) {
      // Match exact path or any subpath under the prefix.
      if (pathname === p || pathname.startsWith(`${p}/`) || pathname.startsWith(p + "?")) {
        return mod.id;
      }
    }
  }
  return null;
}

// ---------------------------------------------------------------------------
// Sidebar primitives — collapsible module header + non-clickable group labels
// Defined at module scope so React keeps the same component identity across
// re-renders (previously nested inside AdminLayout which caused remounts and
// reset the open/closed state every time a NavLink was clicked).
// ---------------------------------------------------------------------------
function ModuleBlock({ module: mod, open, onToggle }) {
  const Icon = mod.icon;
  const Chevron = open ? ChevronDown : ChevronRight;
  return (
    <div data-testid={`nav-module-${mod.id}`} className="mb-1">
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        data-testid={`nav-module-toggle-${mod.id}`}
        className="w-full flex items-center gap-2 px-4 py-2 text-[11px] font-semibold tracking-wide uppercase text-gray-300 hover:text-white transition-colors"
        style={{ fontFamily: "Chivo, sans-serif" }}
      >
        <Icon className="w-3.5 h-3.5" strokeWidth={1.5} />
        <span className="flex-1 text-left">{mod.label}</span>
        <Chevron className="w-3 h-3 text-gray-500" strokeWidth={2} />
      </button>
      {open && (
        <div className="pb-2">
          {mod.groups.length === 0 && mod.placeholder ? (
            <p
              className="px-6 py-1.5 text-[10px] italic text-gray-500 select-none"
              data-testid={`nav-module-placeholder-${mod.id}`}
            >
              {mod.placeholder}
            </p>
          ) : null}
          {mod.groups.map((g) => (
            <div key={g.id} data-testid={`nav-group-${g.id}`}>
              <p
                className="px-4 pt-3 pb-1 text-[9px] font-semibold tracking-widest uppercase text-gray-500 select-none"
                aria-hidden="true"
              >
                {g.label}
              </p>
              {g.items.map((n) => {
                if (n.disabled) {
                  return (
                    <div
                      key={n.to}
                      title="Coming soon"
                      className="flex items-center gap-2.5 pl-6 pr-4 py-1.5 text-xs font-medium text-gray-600 cursor-not-allowed select-none"
                      data-testid={`nav-item-${n.to.replace(/\W+/g, '-')}`}
                      data-disabled="true"
                    >
                      <n.icon className="w-3.5 h-3.5" strokeWidth={1.5} />
                      <span className="flex-1">{n.label}</span>
                      <span className="text-[9px] uppercase tracking-wider text-gray-500">
                        Soon
                      </span>
                    </div>
                  );
                }
                return (
                  <NavLink
                    key={n.to}
                    to={n.to}
                    className={({ isActive }) =>
                      `flex items-center gap-2.5 pl-6 pr-4 py-1.5 text-xs font-medium transition-colors ${
                        isActive
                          ? "bg-[#025979] text-white"
                          : "text-gray-400 hover:text-white hover:bg-gray-800"
                      }`
                    }
                  >
                    <n.icon className="w-3.5 h-3.5" strokeWidth={1.5} />
                    {n.label}
                  </NavLink>
                );
              })}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export function AdminLogin() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handle = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const user = await adminLogin(email, password);
      // Route by role — admins to the Admin Panel, consultants to /app.
      navigate(user?.role === "admin" ? "/admin/questions" : "/app/assessments");
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center">
      <div className="w-full max-w-sm">
        <div className="bg-white border border-[#E5E7EB] rounded-sm shadow-sm p-8">
          <div className="flex items-center gap-2 mb-6">
            <Lock className="w-5 h-5 text-[#025979]" />
            <h1 className="text-xl font-bold text-[#111827]" style={{ fontFamily: "Chivo, sans-serif" }}>Admin Login</h1>
          </div>
          <form onSubmit={handle} className="space-y-4">
            <div>
              <label className="text-xs font-medium text-[#374151] block mb-1">Email</label>
              <Input data-testid="admin-login-email" value={email} onChange={e => setEmail(e.target.value)} className="rounded-sm" />
            </div>
            <div>
              <label className="text-xs font-medium text-[#374151] block mb-1">Password</label>
              <Input data-testid="admin-login-password" type="password" value={password} onChange={e => setPassword(e.target.value)} className="rounded-sm" />
            </div>
            {error && <p data-testid="admin-login-error" className="text-xs text-red-500">{error}</p>}
            <Button data-testid="admin-login-btn" type="submit" disabled={loading} className="w-full bg-[#025979] hover:bg-[#03738C] text-white rounded-sm text-sm">
              {loading ? "Signing in..." : "Sign In"}
            </Button>
          </form>
          <div className="mt-4 text-center">
            <button
              type="button"
              data-testid="admin-forgot-link"
              onClick={() => navigate("/admin/forgot-password")}
              className="text-xs text-[#025979] hover:text-[#03738C] transition-colors"
            >
              Forgot password?
            </button>
          </div>
          <p className="mt-4 text-xs text-center text-[#9CA3AF]">PrimeS4 Admin Panel</p>
        </div>
      </div>
    </div>
  );
}

export function AdminLayout() {
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);
  const [profileOpen, setProfileOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const logo = useProductLogo();

  // Sidebar open/closed state — primarily driven by the active route so the
  // SmartImprove (or SmartEvaluate) section stays expanded when a child item
  // is clicked. User can still manually toggle a section open/closed.
  const initialActive = detectActiveModule(location.pathname) || "smartevaluate";
  const [openMap, setOpenMap] = useState(() => ({
    platform: initialActive === "platform",
    smartevaluate: initialActive === "smartevaluate",
    smartbid: initialActive === "smartbid",
    smartimprove: initialActive === "smartimprove",
    smartewm: initialActive === "smartewm",
  }));

  // Re-evaluate when the route changes — auto-expand the matching module and
  // (only when the active module changes) collapse the previously-open one so
  // the sidebar always reflects "where you are".
  useEffect(() => {
    const active = detectActiveModule(location.pathname);
    if (!active) return;
    // Defer out of the synchronous effect body (state derived from the route
    // is applied on the next microtask) so we don't set state during render.
    let cancelled = false;
    Promise.resolve().then(() => {
      if (cancelled) return;
      setOpenMap((prev) => {
        if (prev[active]) return prev; // already open — preserve user's toggles
        const next = { ...prev };
        // Open the active module; collapse other major modules so the active
        // section is the dominant focus (matches "other major sections should
        // collapse if needed").
        for (const mod of MODULES) {
          next[mod.id] = mod.id === active;
        }
        return next;
      });
    });
    return () => { cancelled = true; };
  }, [location.pathname]);

  useEffect(() => {
    adminMe()
      .then(u => {
        setUser(u);
        // Admin Panel is restricted to admins. Non-admins (consultants) are
        // bounced to /app where they still have full access to Evaluations.
        // Redirect BEFORE clearing the loading state so the admin UI never
        // renders for a non-admin (even for a single frame).
        if (u?.role && u.role !== "admin") {
          navigate("/app/assessments", { replace: true });
          return;
        }
        setChecking(false);
      })
      .catch(() => { setChecking(false); navigate("/transformation"); });
  }, [navigate]);

  const logout = async () => {
    await adminLogout();
    navigate("/transformation");
  };

  if (checking) return <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center"><p className="text-sm text-[#6B7280]">Loading...</p></div>;
  // Hard gate: only render the Admin shell if the user is an admin. Anything
  // else (unauthenticated, wrong role, still-redirecting) renders nothing.
  if (!user || user.role !== "admin") return null;

  return (
    <AuthCtx.Provider value={user}>
      <div className="min-h-screen bg-[#F8FAFC] flex">
        {/* Sidebar */}
        <aside className="sticky top-0 self-start h-screen w-56 bg-[#111827] flex flex-col shrink-0" data-testid="admin-sidebar">
          <div className="px-4 py-4 border-b border-gray-700">
            <NavLink
              to="/admin/questions"
              className="block focus:outline-none"
              data-testid="admin-sidebar-brand"
            >
              {logo ? (
                <img loading="lazy"
                  src={logo}
                  alt="Platform logo"
                  className="h-8 w-auto object-contain"
                  data-testid="admin-sidebar-brand-logo"
                />
              ) : (
                <p
                  className="text-sm font-bold text-white"
                  style={{ fontFamily: "Chivo, sans-serif" }}
                >
                  WarehouseX
                </p>
              )}
            </NavLink>
            <p className="text-[10px] text-gray-400 mt-1.5">{user.email}</p>
          </div>
          <nav className="flex-1 py-2 overflow-y-auto" data-testid="admin-nav">
            {MODULES.map((mod) => (
              <ModuleBlock
                key={mod.id}
                module={mod}
                open={!!openMap[mod.id]}
                onToggle={() =>
                  setOpenMap((m) => ({ ...m, [mod.id]: !m[mod.id] }))
                }
              />
            ))}
          </nav>
          <div className="p-3 border-t border-gray-700 space-y-2">
            <p
              data-testid="sidebar-powered-by"
              className="text-[10px] leading-snug text-gray-500 opacity-80 select-none"
            >
              Powered by PRIVA Operational Intelligence
            </p>
            <button onClick={() => setProfileOpen(true)} className="flex items-center gap-2 text-xs text-gray-400 hover:text-white transition-colors" data-testid="admin-my-profile-btn">
              <UserCircle className="w-3.5 h-3.5" /> My Profile
            </button>
            <button onClick={logout} className="flex items-center gap-2 text-xs text-gray-400 hover:text-white transition-colors" data-testid="admin-logout-btn">
              <LogOut className="w-3.5 h-3.5" /> Logout
            </button>
          </div>
        </aside>
        {/* Content */}
        <main className="flex-1 p-6 sm:p-8">
          <Outlet />
        </main>
      </div>
      <ProfileDialog open={profileOpen} onOpenChange={setProfileOpen} user={user} onProfileUpdated={setUser} />
    </AuthCtx.Provider>
  );
}
